import { useEffect, useMemo, useState } from 'react';
import { Plus, Users } from 'lucide-react';
import apiService from '../../services/api';
import { useToast } from '../../hooks/useToast';
import Toast from '../../components/Toast';
import {
  applyInvoiceAutonomyToggle,
  buildPermissionOverrides,
  describePermissionState,
  effectiveFromProfile,
} from '../../utils/nr2jPermissionEditor';

const EMPTY_FORM = {
  prenom: '',
  nom: '',
  email: '',
  platformProfile: 'COMMERCIAL_NR2J',
  statut: 'ACTIF',
  managerUserId: '',
  quoteApprovalThresholdCents: '',
  discountApprovalThresholdBps: '',
  sendQuotesWithoutApproval: false,
  issueInvoicesWithoutApproval: false,
  issueCreditNotesWithoutApproval: false,
};

const FILTERS = [
  { id: 'all', label: 'Tous' },
  { id: 'platform', label: 'Plateforme' },
  { id: 'manager', label: 'Managers' },
  { id: 'commercial', label: 'Commerciaux' },
  { id: 'admin', label: 'Administratifs' },
];

function memberGroup(member) {
  if (member.role === 'SUPER_ADMIN' || member.role === 'ADMIN') return 'platform';
  if (member.platformProfile === 'MANAGER_NR2J') return 'manager';
  if (member.platformProfile === 'COMMERCIAL_NR2J') return 'commercial';
  if (member.platformProfile === 'ADMINISTRATIF_NR2J') return 'admin';
  return 'platform';
}

function roleLabel(member) {
  if (member.role === 'SUPER_ADMIN') return 'Super admin';
  if (member.role === 'ADMIN') return 'Admin plateforme';
  if (member.platformProfile === 'MANAGER_NR2J') return 'Manager';
  if (member.platformProfile === 'COMMERCIAL_NR2J') return 'Commercial';
  if (member.platformProfile === 'ADMINISTRATIF_NR2J') return 'Administratif';
  return member.platformProfile || member.role;
}

/** N+1 = Manager NR2J actif ou Super Admin actif (pas soi-même). */
function isEligibleNPlusOneOption(member, editorId) {
  if (!member || member.id === editorId) return false;
  if (member.statut !== 'ACTIF') return false;
  if (member.role === 'SUPER_ADMIN') return true;
  return member.platformProfile === 'MANAGER_NR2J';
}

const STATE_LABEL = {
  inherited: 'Héritée du profil',
  added: 'Ajoutée',
  removed: 'Retirée',
  absent: 'Hors profil',
};

function profileTemplate(meta, profileId) {
  return meta?.profiles?.find((profile) => profile.id === profileId)?.permissions || [];
}

export default function Nr2jTeam() {
  const { toasts, toast, removeToast } = useToast();
  const [meta, setMeta] = useState(null);
  const [collaborators, setCollaborators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState(null);
  const [checked, setChecked] = useState([]);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('all');

  const load = async () => {
    setLoading(true);
    try {
      const [catalog, list] = await Promise.all([
        apiService.getNr2jCollaboratorMeta(),
        apiService.listNr2jTeam(),
      ]);
      setMeta(catalog);
      setCollaborators(list?.members || list?.collaborators || []);
    } catch (error) {
      toast.error(error?.message || 'Impossible de charger les collaborateurs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const template = useMemo(
    () => profileTemplate(meta, editor?.platformProfile),
    [meta, editor?.platformProfile],
  );

  const openCreate = () => {
    const profile = 'COMMERCIAL_NR2J';
    const codes = profileTemplate(meta, profile);
    setEditor({ mode: 'create', ...EMPTY_FORM, platformProfile: profile });
    setChecked(codes);
  };

  const openEdit = (member) => {
    setEditor({
      mode: 'edit',
      id: member.id,
      prenom: member.prenom || '',
      nom: member.nom || '',
      email: member.email || '',
      platformProfile: member.platformProfile,
      statut: member.statut || 'ACTIF',
      managerUserId: member.managerUserId || '',
      quoteApprovalThresholdCents: member.quoteApprovalThresholdCents ?? '',
      discountApprovalThresholdBps: member.discountApprovalThresholdBps ?? '',
    });
    setChecked(effectiveFromProfile(
      profileTemplate(meta, member.platformProfile),
      member.overrides,
    ));
  };

  const onProfileChange = (platformProfile) => {
    setEditor((prev) => ({ ...prev, platformProfile }));
    setChecked(profileTemplate(meta, platformProfile));
  };

  const togglePermission = (code) => {
    setChecked((prev) => {
      if (code === 'NR2J_INVOICES_ISSUE_WITHOUT_APPROVAL') {
        const enabling = !prev.includes(code);
        return applyInvoiceAutonomyToggle(prev, enabling);
      }
      return prev.includes(code) ? prev.filter((item) => item !== code) : [...prev, code];
    });
  };

  const save = async (event) => {
    event.preventDefault();
    if (!editor) return;
    const overrides = buildPermissionOverrides(template, checked);
    const payload = {
      prenom: editor.prenom.trim(),
      nom: editor.nom.trim(),
      email: editor.email.trim().toLowerCase(),
      platformProfile: editor.platformProfile,
      statut: editor.statut,
      managerUserId: editor.managerUserId || null,
      discountApprovalThresholdBps: editor.discountApprovalThresholdBps === '' ? null : Number(editor.discountApprovalThresholdBps),
      overrides,
    };
    if (!payload.prenom || !payload.nom || !payload.email) {
      toast.error('Prénom, nom et email sont obligatoires.');
      return;
    }
    setSaving(true);
    try {
      if (editor.mode === 'create') {
        const created = await apiService.createNr2jCollaborator(payload);
        if (created?.inviteEligible && payload.statut === 'ACTIF') {
          try {
            await apiService.adminInviteCollaborator(payload.email);
            toast.success('Collaborateur créé. Une invitation « Bienvenue » a été envoyée.');
          } catch {
            toast.warning('Collaborateur créé, mais l’email d’invitation n’a pas pu partir.');
          }
        } else {
          toast.success('Collaborateur créé.');
        }
      } else {
        await apiService.updateNr2jCollaborator(editor.id, payload);
        toast.success('Collaborateur mis à jour.');
      }
      setEditor(null);
      await load();
    } catch (error) {
      toast.error(error?.message || 'Enregistrement impossible');
    } finally {
      setSaving(false);
    }
  };

  const toggleStatut = async (member) => {
    const statut = member.statut === 'ACTIF' ? 'INACTIF' : 'ACTIF';
    try {
      if (member.role === 'NR2J_STAFF') {
        await apiService.updateNr2jCollaborator(member.id, { statut });
      } else {
        await apiService.updateUser(member.id, { statut });
      }
      toast.success(statut === 'ACTIF' ? 'Compte réactivé' : 'Compte désactivé');
      await load();
    } catch (error) {
      toast.error(error?.message || 'Changement de statut impossible');
    }
  };

  return (
    <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6">
      {toasts.map((item) => (
        <Toast key={item.id} toast={item} onClose={() => removeToast(item.id)} />
      ))}
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold text-gray-900">
            <Users className="h-6 w-6" />
            Équipe Clickorico
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            Comptes et collaborateurs internes Clickorico.
          </p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          disabled={!meta}
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-gray-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          <Plus className="h-4 w-4" />
          Inviter un collaborateur
        </button>
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        {FILTERS.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setFilter(item.id)}
            className={`rounded-full px-3 py-1 text-sm ${filter === item.id ? 'bg-gray-900 text-white' : 'bg-white text-gray-700 border border-gray-200'}`}
          >
            {item.label}
          </button>
        ))}
      </div>

      {loading ? <p className="text-sm text-gray-500">Chargement…</p> : null}

      {!loading && collaborators.length === 0 ? (
        <p className="rounded-xl border border-dashed border-gray-200 px-4 py-8 text-center text-sm text-gray-500">
          Aucun collaborateur NR2J.
        </p>
      ) : null}

      <ul className="space-y-3">
        {collaborators.filter((member) => filter === 'all' || memberGroup(member) === filter).map((member) => {
          const initials = `${(member.prenom || '?')[0]}${(member.nom || '?')[0]}`.toUpperCase();
          return (
            <li key={member.id} className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-sm font-semibold text-gray-700">{initials}</span>
                <div>
                  <p className="font-medium text-gray-900">{member.prenom} {member.nom}</p>
                  <p className="text-sm text-gray-600">{member.email}</p>
                  <div className="mt-1 flex flex-wrap gap-2">
                    <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-800">{roleLabel(member)}</span>
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${member.statut === 'ACTIF' ? 'bg-emerald-50 text-emerald-800' : 'bg-gray-100 text-gray-600'}`}>
                      {member.statut === 'ACTIF' ? 'Actif' : member.statut}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                {member.role === 'NR2J_STAFF' ? (
                  <button type="button" onClick={() => openEdit(member)} className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm">
                    Fiche
                  </button>
                ) : null}
                <button type="button" onClick={() => toggleStatut(member)} className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm">
                  {member.statut === 'ACTIF' ? 'Désactiver' : 'Réactiver'}
                </button>
              </div>
            </li>
          );
        })}
      </ul>

      {editor ? (
        <form onSubmit={save} className="mt-6 space-y-5 rounded-xl border border-gray-200 bg-white p-4">
          <h2 className="text-lg font-medium text-gray-900">
            {editor.mode === 'create' ? 'Nouveau collaborateur' : 'Modifier le collaborateur'}
          </h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="text-sm text-gray-700">
              Prénom
              <input className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2" value={editor.prenom} onChange={(e) => setEditor((prev) => ({ ...prev, prenom: e.target.value }))} />
            </label>
            <label className="text-sm text-gray-700">
              Nom
              <input className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2" value={editor.nom} onChange={(e) => setEditor((prev) => ({ ...prev, nom: e.target.value }))} />
            </label>
            <label className="text-sm text-gray-700">
              Email
              <input type="email" className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2" value={editor.email} onChange={(e) => setEditor((prev) => ({ ...prev, email: e.target.value }))} />
            </label>
            <label className="text-sm text-gray-700">
              Profil
              <select className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2" value={editor.platformProfile} onChange={(e) => onProfileChange(e.target.value)}>
                {(meta?.profiles || []).map((profile) => (
                  <option key={profile.id} value={profile.id}>{profile.label}</option>
                ))}
              </select>
            </label>
            <label className="text-sm text-gray-700">
              Statut
              <select className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2" value={editor.statut} onChange={(e) => setEditor((prev) => ({ ...prev, statut: e.target.value }))}>
                <option value="ACTIF">Actif</option>
                <option value="INACTIF">Inactif</option>
                <option value="SUSPENDU">Suspendu</option>
              </select>
            </label>
            <label className="text-sm text-gray-700">
              N+1
              <select className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2" value={editor.managerUserId || ''} onChange={(e) => setEditor((prev) => ({ ...prev, managerUserId: e.target.value }))}>
                <option value="">Aucun</option>
                {collaborators.filter((member) => isEligibleNPlusOneOption(member, editor.id)).map((member) => (
                  <option key={member.id} value={member.id}>
                    {member.prenom} {member.nom}
                    {member.role === 'SUPER_ADMIN' ? ' (Super Admin)' : ''}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <fieldset>
            <legend className="text-sm font-medium text-gray-900">Autonomie / validations</legend>
            <label className="mt-2 flex items-center gap-2 text-sm">
              <input type="checkbox" checked={checked.includes('NR2J_QUOTES_SEND_WITHOUT_APPROVAL')} onChange={() => togglePermission('NR2J_QUOTES_SEND_WITHOUT_APPROVAL')} />
              Peut envoyer devis sans validation
            </label>
            <label className="mt-2 flex items-center gap-2 text-sm">
              <input type="checkbox" checked={checked.includes('NR2J_QUOTES_DISCOUNT_WITHOUT_APPROVAL')} onChange={() => togglePermission('NR2J_QUOTES_DISCOUNT_WITHOUT_APPROVAL')} />
              Peut consentir une remise sans validation
            </label>
            <label className="mt-2 flex items-center gap-2 text-sm">
              <input type="checkbox" checked={checked.includes('NR2J_INVOICES_ISSUE_WITHOUT_APPROVAL')} onChange={() => togglePermission('NR2J_INVOICES_ISSUE_WITHOUT_APPROVAL')} />
              Peut émettre facture sans validation
            </label>
            <label className="mt-2 flex items-center gap-2 text-sm">
              <input type="checkbox" checked={checked.includes('NR2J_CREDIT_NOTES_ISSUE_WITHOUT_APPROVAL')} onChange={() => togglePermission('NR2J_CREDIT_NOTES_ISSUE_WITHOUT_APPROVAL')} />
              Peut émettre avoir sans validation
            </label>
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <label className="text-sm text-gray-700 sm:col-span-2">
                Seuil de remise (basis points, 1000 = 10 %)
                <input className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2" value={editor.discountApprovalThresholdBps} onChange={(e) => setEditor((prev) => ({ ...prev, discountApprovalThresholdBps: e.target.value }))} />
                <span className="mt-1 block text-xs text-gray-500">
                  Au-delà de ce taux de remise, une validation est requise.
                  Sans seuil et avec autonomie remise, toute remise est autorisée.
                </span>
              </label>
            </div>
          </fieldset>

          <fieldset>
            <legend className="text-sm font-medium text-gray-900">Permissions</legend>
            <p className="mb-3 text-xs text-gray-500">
              Case cochée et héritée du profil, ajoutée en plus, ou retirée du profil.
            </p>
            <div className="divide-y divide-gray-100">
              {(meta?.permissions || [])
                .filter((permission) => ![
                  'NR2J_PROSPECTS_DELETE',
                  'NR2J_USERS_MANAGE',
                  'NR2J_ACTIVITY_ALL_VIEW',
                ].includes(permission.code))
                .map((permission) => {
                const state = describePermissionState(permission.code, template, checked);
                const on = checked.includes(permission.code);
                return (
                  <label key={permission.code} className="flex items-start gap-3 py-2">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={on}
                      onChange={() => togglePermission(permission.code)}
                    />
                    <span>
                      <span className="block text-sm text-gray-900">{permission.label}</span>
                      <span className={`text-xs ${state === 'removed' ? 'text-amber-700' : state === 'added' ? 'text-emerald-700' : 'text-gray-500'}`}>
                        {STATE_LABEL[state]}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          </fieldset>

          <div className="flex gap-2">
            <button type="submit" disabled={saving} className="rounded-xl bg-gray-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </button>
            <button type="button" onClick={() => setEditor(null)} className="rounded-xl border border-gray-200 px-4 py-2 text-sm">
              Annuler
            </button>
          </div>
        </form>
      ) : null}
    </div>
  );
}
